import { createClient } from '@/lib/supabase/client';

export interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  featured_image_url: string;
  category: string;
  tags: string[];
  author_name: string;
  status: 'draft' | 'published';
  meta_title: string;
  meta_description: string;
  read_time_minutes: number;
  published_at: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

export interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: 'superadmin' | 'editor';
  is_active: boolean;
  last_login_at: string | null;
  created_at: string;
}

export const BLOG_CATEGORIES = [
  'Finance Basics',
  'Home Loans',
  'Car Loans',
  'Personal Loans',
  'EMI Tips',
  'Savings & Investment',
  'Credit Score',
  'General',
];

function getClient() {
  return createClient();
}

export const blogService = {
  // Get all posts (admin - all statuses)
  async getAllPosts(filters?: {
    status?: string;
    category?: string;
    author?: string;
    search?: string;
    page?: number;
    pageSize?: number;
  }) {
    const supabase = getClient();
    let query = supabase
      .from('blog_posts')
      .select('*', { count: 'exact' })
      .order('created_at', { ascending: false });

    if (filters?.status && filters.status !== 'all') {
      query = query.eq('status', filters.status);
    }
    if (filters?.category && filters.category !== 'all') {
      query = query.eq('category', filters.category);
    }
    if (filters?.author && filters.author !== 'all') {
      query = query.eq('author_name', filters.author);
    }
    if (filters?.search) {
      query = query.or(`title.ilike.%${filters.search}%,content.ilike.%${filters.search}%`);
    }

    const page = filters?.page || 1;
    const pageSize = filters?.pageSize || 20;
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;
    query = query.range(from, to);

    const { data, error, count } = await query;
    if (error) throw error;
    return { posts: (data || []) as BlogPost[], total: count || 0 };
  },

  // Get published posts (public blog)
  async getPublishedPosts() {
    const supabase = getClient();
    const { data, error } = await supabase
      .from('blog_posts')
      .select('*')
      .eq('status', 'published')
      .order('published_at', { ascending: false });
    if (error) throw error;
    return (data || []) as BlogPost[];
  },

  // Get single post by slug (public)
  async getPostBySlug(slug: string) {
    const supabase = getClient();
    const { data, error } = await supabase
      .from('blog_posts')
      .select('*')
      .eq('slug', slug)
      .eq('status', 'published')
      .single();
    if (error) return null;
    return data as BlogPost;
  },

  // Get post by id (admin preview)
  async getPostById(id: string) {
    const supabase = getClient();
    const { data, error } = await supabase
      .from('blog_posts')
      .select('*')
      .eq('id', id)
      .single();
    if (error) return null;
    return data as BlogPost;
  },

  // Create post
  async createPost(post: Partial<BlogPost>) {
    const supabase = getClient();
    const { data, error } = await supabase
      .from('blog_posts')
      .insert([post])
      .select()
      .single();
    if (error) throw error;
    return data as BlogPost;
  },

  // Update post
  async updatePost(id: string, updates: Partial<BlogPost>) {
    const supabase = getClient();
    const { data, error } = await supabase
      .from('blog_posts')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data as BlogPost;
  },

  // Delete post
  async deletePost(id: string) {
    const supabase = getClient();
    const { error } = await supabase
      .from('blog_posts')
      .delete()
      .eq('id', id);
    if (error) throw error;
  },

  // Bulk delete
  async bulkDelete(ids: string[]) {
    const supabase = getClient();
    const { error } = await supabase
      .from('blog_posts')
      .delete()
      .in('id', ids);
    if (error) throw error;
  },

  // Bulk update status
  async bulkUpdateStatus(ids: string[], status: 'draft' | 'published') {
    const supabase = getClient();
    const updates: Partial<BlogPost> = { status };
    if (status === 'published') {
      updates.published_at = new Date().toISOString();
    }
    const { error } = await supabase
      .from('blog_posts')
      .update(updates)
      .in('id', ids);
    if (error) throw error;
  },

  // Check slug uniqueness
  async isSlugUnique(slug: string, excludeId?: string) {
    const supabase = getClient();
    let query = supabase.from('blog_posts').select('id').eq('slug', slug);
    if (excludeId) {
      query = query.neq('id', excludeId);
    }
    const { data } = await query;
    return !data || data.length === 0;
  },

  // Get dashboard stats
  async getStats() {
    const supabase = getClient();
    const { data, error } = await supabase
      .from('blog_posts')
      .select('status, published_at');
    if (error) throw error;
    const posts = data || [];
    const now = new Date();
    const thisMonth = posts.filter((p) => {
      if (!p.published_at) return false;
      const d = new Date(p.published_at);
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    });
    return {
      totalPublished: posts.filter((p) => p.status === 'published').length,
      totalDrafts: posts.filter((p) => p.status === 'draft').length,
      postsThisMonth: thisMonth.length,
    };
  },

  // Get recent posts
  async getRecentPosts(limit = 10) {
    const supabase = getClient();
    const { data, error } = await supabase
      .from('blog_posts')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);
    if (error) throw error;
    return (data || []) as BlogPost[];
  },
};

export const adminUserService = {
  async getAll() {
    const supabase = getClient();
    const { data, error } = await supabase
      .from('admin_users')
      .select('*')
      .order('created_at', { ascending: true });
    if (error) throw error;
    return (data || []) as AdminUser[];
  },

  async create(user: { email: string; name: string; role: 'superadmin' | 'editor'; password: string }) {
    const supabase = getClient();
    // Create auth user
    const { data: authData, error: authError } = await supabase.auth.signUp({ email: user.email, password: user.password });
    if (authError) throw authError;
    // Create admin_users row
    const { data, error } = await supabase
      .from('admin_users')
      .insert([{ email: user.email, name: user.name, role: user.role, is_active: true }])
      .select()
      .single();
    if (error) throw error;
    return data as AdminUser;
  },

  async update(id: string, updates: Partial<AdminUser>) {
    const supabase = getClient();
    const { data, error } = await supabase
      .from('admin_users')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data as AdminUser;
  },

  async deactivate(id: string) {
    return adminUserService.update(id, { is_active: false });
  },

  async activate(id: string) {
    return adminUserService.update(id, { is_active: true });
  },
};

export function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

export function calculateReadTime(content: string): number {
  const text = content.replace(/<[^>]*>/g, ' ');
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.ceil(words / 200));
}
